import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Breadcrumbs from "components/Common/Breadcrumb";
import DataTable from "../../components/Common/LocalizedDataTable";
import { withTranslation } from "react-i18next";
import { Card, CardBody } from "reactstrap";
import Swal, { showToast } from "../../helpers/swal_helper";
import moment from "moment";
import { formatNumber } from "../../helpers/language_helper";
import { VendorAccountingApi } from "apis/VendorAccountingApi";
import config from "config/config";

// ---------------------------------------------------------------------------
// Server-side list + summary, driven by:
//   POST /superadmin/get-vendor-accounting          (paginated rows)
//   POST /superadmin/get-vendor-accounting-summary  (totals for the same filters)
//   POST /superadmin/settle-vendor-accounting        (settle / unsettle one row)
//   POST /superadmin/settle-all-vendor-accounting     (settle every pending row matching the filters)
// All four accept the same filter shape: search, settlement_status, from_date,
// to_date (list/summary also take salon_id, page, limit). from_date/to_date
// are matched inclusively against bookings.booking_date - there is no
// date_filter param, so quick options ("today" etc.) are resolved to a
// concrete from_date/to_date client-side before the request goes out.
// ---------------------------------------------------------------------------

// Actual /superadmin/get-vendor-accounting response shape (confirmed on stage):
// { status, message, data: [{ booking_id, salon_name, amount, booking_date,
//   settled_date, status: "Settled" | "Unsettled" }], pagination: {...} }
// There's no separate vendor_accounting_id in this payload - booking_id is the
// only identifier available, so it doubles as the row key.
const mapRow = (v) => ({
  id: v.booking_id,
  booking_id: v.booking_id,
  vendor_name: v.salon_name || "-",
  amount: v.amount,
  booking_date: v.booking_date || null,
  is_settled: String(v.status).toLowerCase() === "settled",
  settled_date: v.settled_date,
});

// The API has no `date_filter` param - it only understands concrete
// `from_date`/`to_date` (inclusive, matched against bookings.booking_date).
// So "Today" / "This Week" / "This Month" have to be resolved to an actual
// date range client-side before being sent.
const quickRangeToDates = (dateQuickFilter) => {
  const fmt = "YYYY-MM-DD";
  switch (dateQuickFilter) {
    case "today":
      return { from: moment().format(fmt), to: moment().format(fmt) };
    case "this_week":
      return { from: moment().startOf("week").format(fmt), to: moment().endOf("week").format(fmt) };
    case "this_month":
      return { from: moment().startOf("month").format(fmt), to: moment().endOf("month").format(fmt) };
    default:
      return null;
  }
};

// Builds the shared filter payload (everything but page/limit) from UI state.
// Keys with no value are simply omitted rather than sent as "" / null.
const buildFilters = ({ salonSearch, statusFilter, dateQuickFilter, dateFrom, dateTo }) => {
  const filters = {};

  if (salonSearch.trim()) filters.search = salonSearch.trim();
  if (statusFilter !== "all") filters.settlement_status = statusFilter === "settled" ? "Settled" : "Unsettled";

  if (dateQuickFilter === "custom") {
    // Custom range only makes sense once both bounds are picked, in order -
    // until then fall back to "no date filter" instead of sending a
    // half-built or inverted range to the API.
    if (dateFrom && dateTo && dateFrom <= dateTo) {
      filters.from_date = dateFrom;
      filters.to_date = dateTo;
    }
  } else if (dateQuickFilter !== "all") {
    const range = quickRangeToDates(dateQuickFilter);
    if (range) {
      filters.from_date = range.from;
      filters.to_date = range.to;
    }
  }

  return filters;
};

// True once a custom range has both dates and isn't inverted - i.e. once
// buildFilters() will actually attach date_filter/from_date/to_date for it.
const isCustomRangeReady = (dateQuickFilter, dateFrom, dateTo) =>
  dateQuickFilter !== "custom" || (!!dateFrom && !!dateTo && dateFrom <= dateTo);

function VendorAccountings(props) {
  const { t } = props;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [settlingId, setSettlingId] = useState(null);
  const [settlingAll, setSettlingAll] = useState(false);

  const [summary, setSummary] = useState({
    total_booking: 0,
    total_booking_amount: 0,
    settled_amount: 0,
    pending_amount: 0,
    pending_count: 0,
  });

  const [statusFilter, setStatusFilter] = useState("all"); // all | settled | unsettled
  const [dateQuickFilter, setDateQuickFilter] = useState("all"); // all | today | this_week | this_month | custom
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [salonSearch, setSalonSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(config.LIMIT);
  const [totalRows, setTotalRows] = useState(0);

  // Debounce the free-text search box so every keystroke doesn't fire a request.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSalonSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Any other filter change resets back to page 1.
  useEffect(() => {
    setPage(1);
  }, [statusFilter, dateQuickFilter, dateFrom, dateTo]);

  const customRangeReady = isCustomRangeReady(dateQuickFilter, dateFrom, dateTo);

  useEffect(() => {
    // Don't fire requests for an incomplete/inverted custom range - wait for
    // the user to finish picking valid dates instead of erroring on every
    // keystroke of the date pickers.
    if (!customRangeReady) return;
    loadData();
    loadSummary();
  }, [salonSearch, statusFilter, dateQuickFilter, dateFrom, dateTo, page, limit]);

  const loadData = () => {
    setLoading(true);
    VendorAccountingApi.getVendorAccounting({
      ...buildFilters({ salonSearch, statusFilter, dateQuickFilter, dateFrom, dateTo }),
      page,
      limit,
    })
      .then((res) => {
        if (!res.data.status) throw new Error(res.data.message || "Failed to fetch");
        setRows((res.data.data || []).map(mapRow));
        setTotalRows(res.data.pagination?.total || 0);
      })
      .catch((err) => {
        Swal.fire({
          title: t("Error"),
          text: err?.response?.data?.message || err.message || t("Failed to fetch vendor accountings"),
          icon: "error",
        });
      })
      .finally(() => setLoading(false));
  };

  const loadSummary = () => {
    VendorAccountingApi.getVendorAccountingSummary(
      buildFilters({ salonSearch, statusFilter, dateQuickFilter, dateFrom, dateTo })
    )
      .then((res) => {
        if (!res.data.status) throw new Error(res.data.message || "Failed to fetch summary");
        setSummary(res.data.data || {});
      })
      .catch(() => {
        // Summary is secondary to the list - fail quietly and keep the last known totals.
      });
  };

  // ---- actions ------------------------------------------------------------
  const toggleSettle = (row) => {
    const settle = !row.is_settled;
    setSettlingId(row.id);
    VendorAccountingApi.settleVendorAccounting({ booking_id: row.booking_id, is_settled: settle })
      .then((res) => {
        if (!res.data.status) throw new Error(res.data.message || "Failed to update settlement");
        loadData();
        loadSummary();
        showToast(settle ? t("Booking settled.") : t("Booking marked unsettled."));
      })
      .catch((err) =>
        Swal.fire(t("Error!"), err?.response?.data?.message || t("Failed to update settlement"), "error")
      )
      .finally(() => setSettlingId(null));
  };

  const settleAll = () => {
    if (!summary.pending_count) {
      Swal.fire(t("Nothing to settle"), t("There are no unsettled bookings in the current filter."), "info");
      return;
    }

    Swal.fire({
      title: t("Are you sure?"),
      text: t("Are You sure, you want to settle total pending of SAR {{amount}}/-?", {
        amount: formatNumber(summary.pending_amount),
      }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#f46a6a",
      confirmButtonText: t("Yes, settle all"),
      cancelButtonText: t("Cancel"),
    }).then((result) => {
      if (!result.isConfirmed) return;
      setSettlingAll(true);
      // Restricted to pending rows server-side regardless of the settlement_status
      // filter, so it's safe to send the same filters used for the list/summary.
      VendorAccountingApi.settleAllVendorAccounting(
        buildFilters({ salonSearch, statusFilter, dateQuickFilter, dateFrom, dateTo })
      )
        .then((res) => {
          if (!res.data.status) throw new Error(res.data.message || "Failed to settle bookings");
          setPage(1);
          loadData();
          loadSummary();
          Swal.fire(t("Done!"), res.data.message || t("All filtered pending bookings have been settled."), "success");
        })
        .catch((err) =>
          Swal.fire(t("Error!"), err?.response?.data?.message || t("Failed to settle bookings"), "error")
        )
        .finally(() => setSettlingAll(false));
    });
  };

  // ---- table columns --------------------------------------------------------
  const columns = [
    {
      name: t("Booking ID"),
      selector: (row) => row.booking_id,
      sortable: true,
      cell: (row) => formatNumber(row.booking_id),
    },
    { name: t("Vendor / Salon"), selector: (row) => row.vendor_name, sortable: true },
    {
      name: t("Amount"),
      selector: (row) => row.amount,
      sortable: true,
      cell: (row) => `SAR ${formatNumber(row.amount)}/-`,
    },
    { name: t("Booking Date"), selector: (row) => row.booking_date, sortable: true, cell: (row) => (row.booking_date ? moment(row.booking_date).format("DD MMM Y") : "-") },
    {
      name: t("Settled Date"),
      selector: (row) => row.settled_date,
      sortable: true,
      cell: (row) => (row.settled_date ? moment(row.settled_date).format("DD MMM Y") : "-"),
    },
    {
      name: t("Status"),
      selector: (row) => row.is_settled,
      sortable: true,
      cell: (row) => (
        <span
          style={{
            color: row.is_settled ? "#8B61A1" : "#6c757d",
            fontWeight: "500",
          }}
        >
          {row.is_settled ? t("Settled") : t("Unsettled")}
        </span>
      ),
    },
    {
      name: t("Action"),
      cell: (row) => (
        <button
          type="button"
          className={`btn btn-sm ${row.is_settled ? "btn-outline-danger" : "btn-outline-success"}`}
          disabled={settlingId === row.id}
          onClick={() => toggleSettle(row)}
        >
          {settlingId === row.id ? t("Please wait...") : row.is_settled ? t("Unsettle") : t("Settle")}
        </button>
      ),
    },
  ];

  document.title = t("Vendor Accounting") + " | " + t("Salon");

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title={t("Dashboard")} breadcrumbItem={t("Vendor Accounting")} />
      </div>

      <div className="container-fluid">
        {/* --- summary cards ------------------------------------------------ */}
        <div className="row">
          <div className="col-md-4">
            <Card className="mini-stats-wid">
              <CardBody>
                <div className="d-flex">
                  <div className="flex-grow-1">
                    <p className="text-muted fw-medium">{t("Total Booking Amount")}</p>
                    <h4 className="mb-0">SAR {formatNumber(summary.total_booking_amount)}/-</h4>
                  </div>
                  <div className="avatar-sm rounded-circle align-self-center mini-stat-icon">
                    <span className="avatar-title rounded-circle" style={{ backgroundColor: "#8B61A1" }}>
                      <i className="bx bx-wallet font-size-24" />
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
          <div className="col-md-4">
            <Card className="mini-stats-wid">
              <CardBody>
                <div className="d-flex">
                  <div className="flex-grow-1">
                    <p className="text-muted fw-medium">{t("Total Pending Amount")}</p>
                    <h4 className="mb-0">SAR {formatNumber(summary.pending_amount)}/-</h4>
                  </div>
                  <div className="avatar-sm rounded-circle align-self-center mini-stat-icon">
                    <span className="avatar-title rounded-circle" style={{ backgroundColor: "#8B61A1" }}>
                      <i className="bx bx-time-five font-size-24" />
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
          <div className="col-md-4">
            <Card className="mini-stats-wid">
              <CardBody>
                <div className="d-flex">
                  <div className="flex-grow-1">
                    <p className="text-muted fw-medium">{t("Total Settled Amount")}</p>
                    <h4 className="mb-0">SAR {formatNumber(summary.settled_amount)}/-</h4>
                  </div>
                  <div className="avatar-sm rounded-circle align-self-center mini-stat-icon">
                    <span className="avatar-title rounded-circle" style={{ backgroundColor: "#8B61A1" }}>
                      <i className="bx bx-check-circle font-size-24" />
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>

        {/* --- filters -------------------------------------------------------- */}
        <div className="row mb-3 align-items-center">
          <div className="col-lg-3 col-md-6 mb-2">
            <input
              type="text"
              className="form-control"
              placeholder={t("Search by Salon or Vendor Name")}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>

          <div className="col-lg-2 col-md-6 mb-2">
            <select
              className="form-control"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">{t("All Settlement Status")}</option>
              <option value="settled">{t("Settled")}</option>
              <option value="unsettled">{t("Not Settled")}</option>
            </select>
          </div>

          <div className="col-lg-2 col-md-6 mb-2">
            <select
              className="form-control"
              value={dateQuickFilter}
              onChange={(e) => {
                setDateQuickFilter(e.target.value);
                if (e.target.value !== "custom") {
                  setDateFrom("");
                  setDateTo("");
                }
              }}
            >
              <option value="all">{t("All Dates")}</option>
              <option value="today">{t("Today")}</option>
              <option value="this_week">{t("This Week")}</option>
              <option value="this_month">{t("This Month")}</option>
              <option value="custom">{t("Custom")}</option>
            </select>
          </div>

          {dateQuickFilter === "custom" && (
            <>
              <div className="col-lg-2 col-md-6 mb-2">
                <input
                  type="date"
                  className="form-control"
                  placeholder={t("From Date")}
                  title={t("From Date")}
                  value={dateFrom}
                  max={dateTo || undefined}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </div>

              <div className="col-lg-2 col-md-6 mb-2">
                <input
                  type="date"
                  className="form-control"
                  placeholder={t("To Date")}
                  title={t("To Date")}
                  value={dateTo}
                  min={dateFrom || undefined}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>

              {!customRangeReady && (
                <div className="col-12">
                  <small className="text-muted">{t("Pick both a from and to date to apply the custom range.")}</small>
                </div>
              )}
            </>
          )}
        </div>

        {/* --- table ------------------------------------------------------------ */}
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <DataTable
                  columns={columns}
                  data={rows}
                  pagination
                  paginationServer
                  paginationTotalRows={totalRows}
                  paginationDefaultPage={page}
                  paginationPerPage={limit}
                  onChangePage={(p) => setPage(p)}
                  onChangeRowsPerPage={(newLimit, newPage) => {
                    setLimit(newLimit);
                    setPage(newPage);
                  }}
                  progressPending={loading}
                  noDataComponent={t("There are no records to display")}
                />

                <div className="d-flex justify-content-between align-items-center border-top pt-3 mt-2">
                  <h5 className="mb-0">
                    {t("Total Pending")}: <span className="text-danger">SAR {formatNumber(summary.pending_amount)}/-</span>
                    {summary.pending_count > 0 && (
                      <small className="text-muted ms-2">
                        ({formatNumber(summary.pending_count)} {t("bookings")})
                      </small>
                    )}
                  </h5>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={loading || settlingAll || !summary.pending_count}
                    onClick={settleAll}
                  >
                    {settlingAll ? t("Please wait...") : t("Settle All")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

VendorAccountings.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(VendorAccountings);
